import React, { useEffect, useMemo, useState } from 'react';
import { BubbleProps, Cascader, CascaderMenuOption, CascaderOption, Trigger, TriggerWithProps } from 'tea-component';
import { useIntl } from 'umi';
import { TSelectorItem } from '../../pages/metricDetail/components/MetricDetailFilter/_type';
import { getName } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import CommonBubble from '@home/components/CommonBubble';
import { BaseComponentProps } from '../../types/commom';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import './index.scss';

type TCountryOption = TSelectorItem & {
  area_name_ch: string;
  area_name_en: string;
  area_code: string;
  icon?: string;
  svgIcon?: string;
  disabled?: boolean;
};

interface ICountryFilterOption extends CascaderOption {
  nameEn?: string;
  nameCn?: string;
}

interface ICountryFilterProps extends BaseComponentProps {
  className: string;
  defaultOpen?: boolean;
  sort?: boolean;
  overlayClassName?: string;
  placementOffset?: string | number | [string | number, string | number] | undefined;
  value?: string;
  options?: TCountryOption[];
  placeholderEn?: string;
  placeholderCn?: string;
  tooltipEn?: string;
  tooltipCn?: string;
  trigger?: "hover" | "contextMenu" | "focus" | "click" | "empty" | Trigger | TriggerWithProps | undefined;
  nameCN: string;
  nameEN: string;
  filter?: (inputValue: string, options: ICountryFilterOption) => boolean;
  filterKey: string;
  view_type?: 'singleCascade' | 'groupCascade';
  // 滚动容器的class名称
  containerClassName?: string;
  onChange?: (filterKey: string, value: string) => void;
  bubbleProps?: BubbleProps;
}

const CountryFilter = (props: ICountryFilterProps) => {
  const {
    className,
    overlayClassName,
    placementOffset,
    defaultOpen = false,
    sort = true,
    style,
    filter,
    filterKey,
    value: propsValue,
    options = [],
    placeholderCn,
    placeholderEn,
    tooltipEn,
    tooltipCn,
    trigger = "focus",
    nameCN,
    nameEN,
    // 预留参数，todo: 后续有需要再支持多选
    // view_type = 'singleCascade',
    containerClassName = '.tea-layout__content-body',
    onChange,
    bubbleProps,
  } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [nameWidth, setNameWidth] = useState(10);
  const [searchVal, setSearchVal] = useState<string>('');

  const value = useMemo(() => {
    if (!propsValue) return [];
    const option = options?.find(item => item.code === propsValue);
    if (option) {
      // 一级选项
      if (option.area_code === option.code) {
        return [option.area_code];
      }
      // 二级选项
      return [option.area_code, option.code];
    }
    return [];
  }, [options, propsValue]);

  // 搜索高亮文字
  const getHighlightedText = (text: string, highlight: string) => {
    if (!highlight.trim()) {
      return text;
    }
    // 需转移特殊符号，避免报错
    const regex = new RegExp(`(${highlight?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text?.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? <strong key={index}>{part}</strong> : part
    );
  };

  const newOptions = useMemo(() => {
    const result: CascaderMenuOption[] = [];
    const isSearch = !!searchVal;
    // 根据区域分类国家
    const grouped = options?.reduce((acc, item) => {
      const {
        area_name_ch,
        area_name_en,
        area_code,
        name_ch,
        name_en,
        code,
        icon,
        svgIcon,
        disabled,
      } = item;
      if (!acc[area_code]) {
        const areaName = getName(isEn, area_name_en, area_name_ch);
        // 高亮名称
        const lightAreaName = isSearch ? getHighlightedText(areaName, searchVal) : areaName;
        acc[area_code] = {
          value: area_code,
          nameEn: area_name_en,
          nameCn: area_name_ch
        };
        // 区域code和国家code一样则不添加子节点，视为一级选项
        if (area_code === code) {
          acc[area_code].label = (
            <div className="dbb-country-filter-overlay-row">
              {icon && <img src={icon} />}
              {svgIcon && <SvgIcon type={svgIcon} className="dbb-country-filter-overlay-row-svg" />}
              <div>{lightAreaName}</div>
            </div>
          );
        } else {
          acc[area_code].label = lightAreaName;
          acc[area_code].children = [];
        }
      }
      // 区域code和国家code一样则不添加子节点，视为一级选项
      if (area_code === code) return acc;

      const name = getName(isEn, name_en, name_ch);
      acc[area_code].children.push({
        label: (
          <div className="dbb-country-filter-overlay-row">
            {icon && <img src={icon} />}
            {svgIcon && <SvgIcon type={svgIcon} className="dbb-country-filter-overlay-row-svg" />}
            <div>{isSearch ? getHighlightedText(name, searchVal) : name}</div>
          </div>
        ),
        nameEn: name_en,
        nameCn: name_ch,
        value: code,
        disabled,
      });
      return acc;
    }, {} as any);

    Object.keys(grouped).forEach((key) => {
      if (key !== 'global') {
        result.push(grouped[key]);
      }
    });

    if(sort) {
      // 对区域进行排序
      result.sort((region1: any, region2: any) => {
        const region1Name = getName(isEn, region1.nameEn, region1.nameCn);
        const region2Name = getName(isEn, region2.nameEn, region2.nameCn);
        return region1Name.localeCompare(region2Name);
      });

      // 国家排序
      result.forEach(item => {
        if (item.children && item.children?.length > 0) {
          item.children = item.children?.sort((country1: any, country2: any) => {
            const country1Name = getName(isEn, country1.nameEn, country1.nameCn);
            const country2Name = getName(isEn, country2.nameEn, country2.nameCn);
            return country1Name.localeCompare(country2Name);
          });
        }
      });
    }

    return grouped.global ? [grouped.global, ...result] : result;
  }, [options, isEn, searchVal]);

  const handleChange = (val: string[]) => {
    setSearchVal('');
    onChange?.(filterKey, val[val.length - 1]);
  };

  // 搜索时，光标对齐选项值
  useEffect(() => {
    const nameDom = document.getElementById('dbb-country-filter-input-name');
    if (nameDom) {
      const width = nameDom.clientWidth;
      const filterDom = document.querySelector('.dbb-country-filter-container');
      if (filterDom) {
        const inputDom = filterDom.querySelector('input');
        if (inputDom) {
          // pd:10 + mr:6 + icon:16 + mr:5
          inputDom.style.paddingLeft = `${width + 10 + 6}px`;
          setNameWidth(-(width + 10 + 14));
        }
      }
    }
  }, [isEn]);

  // 修复官方组件bug：页面滚动时，需要失焦，再聚焦才能触发面板展示
  useEffect(() => {
    const contentBody = document.querySelector(containerClassName);
    const inputDom = document.querySelector('.dbb-country-filter-container input');
    const onBlur = () => {
      inputDom?.blur?.();
    };
    const onInputBlur = () => {
      setSearchVal('');
    };
    if (contentBody) {
      contentBody.addEventListener('scroll', onBlur);
    }
    if (inputDom) {
      inputDom.addEventListener('blur', onInputBlur);
    }
    return () => {
      if (contentBody) {
        contentBody.removeEventListener('scroll', onBlur);
      }
      if (inputDom) {
        inputDom.removeEventListener('blur', onInputBlur);
      }
    };
  }, []);

  // 修正官方组件onSearch为空值时，不触发事件
  useEffect(() => {
    const inputDom = document.querySelector('.dbb-country-filter-container input');
    const onInputChange = (e: any) => {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (searchVal?.length === 1) {
          setSearchVal('');
        }
      }
    };
    if (inputDom) {
      inputDom.removeEventListener('keydown', onInputChange);
      inputDom.addEventListener('keydown', onInputChange);
    }
    return () => {
      if (inputDom) {
        inputDom.removeEventListener('keydown', onInputChange);
      }
    };
  }, [searchVal]);

  // 打开下拉窗口时默认展开第一个一级菜单选项的子选项
  useEffect(() => {
    // 有值时不需要模拟展开子菜单
    if(!!searchVal || propsValue || !defaultOpen) return;
    const expandFirstOption = () => {
      // 查找下拉面板
      const overlay = document.querySelector('.dbb-country-filter-overlay');
      if (!overlay) return;
      // 查找所有有子项的选项（第一级菜单项）
      const firstLevelMenu = overlay.querySelector('.tea-cascader-menu--thin .tea-list--option');
      if (!firstLevelMenu) return;
      try {
        // 查找所有列表项
        const allItems = firstLevelMenu.querySelectorAll('.tea-list__submenu');
        const isSelected = Array?.from(allItems)?.some((item) => item.classList.contains('is-selected'));
        const firstItem = allItems?.[0];
        // 找到第一个有子菜单的项并展开
        if (firstItem && !firstItem?.classList?.contains('is-selected') && !isSelected) {
        // 触发点击事件来展开子菜单
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          firstItem.dispatchEvent(clickEvent);
        }
      } catch (error) {
        
      }
    };

    // 使用 MutationObserver 监听下拉面板的显示
    const observer = new MutationObserver(() => {
      const overlay = document.querySelector('.dbb-country-filter-overlay');
      if (overlay) {
        const style = window.getComputedStyle(overlay);
        if (style.display !== 'none' && style.visibility !== 'hidden') {
          // 延迟执行，确保 DOM 完全渲染
          setTimeout(() => {
            expandFirstOption();
          }, 100);
        }
      }
    });

    // 开始观察
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    // 也监听点击和聚焦事件，当下拉打开时展开
    const inputDom = document.querySelector('.dbb-country-filter-container input');
    const handleInputInteraction = () => {
      setTimeout(() => {
        expandFirstOption();
      }, 200);
    };

    if (inputDom) {
      inputDom.addEventListener('focus', handleInputInteraction);
      inputDom.addEventListener('click', handleInputInteraction);
    }

    return () => {
      observer.disconnect();
      if (inputDom) {
        inputDom.removeEventListener('focus', handleInputInteraction);
        inputDom.removeEventListener('click', handleInputInteraction);
      }
    };
  }, [propsValue, defaultOpen, searchVal]);

  // 移除 <strong> 标签
  const removeStrongTags: any = (element: React.ReactNode) => {
    if (!searchVal?.trim()) {
      return element;
    }
    if (typeof element === 'string' || typeof element === 'number') {
      return element;
    }

    if (element?.type === 'strong') {
      return element?.props?.children;
    }

    if (React.isValidElement(element)) {
      return React.cloneElement(
        element,
        { ...element.props },
        React.Children.map(element.props.children, removeStrongTags)
      );
    }

    return element;
  };

  return (
    <CommonBubble content={getName(isEn, tooltipEn, tooltipCn)} trigger={trigger} {...(bubbleProps || {})}>
      <div className={classNames(className, 'dbb-country-filter-container')} style={style}>
        <div id='dbb-country-filter-input-name'>{getName(isEn, nameEN, nameCN)}</div>
        <Cascader
          type="menu"
          data={newOptions}
          // multiple={view_type === 'groupCascade'}
          multiple={false}
          onChange={handleChange}
          value={value}
          searchable
          placementOffset={placementOffset || [nameWidth, 10]}
          placeholder={getName(isEn, placeholderEn, placeholderCn)}
          expandTrigger="click"
          overlayClassName={classNames('dbb-country-filter-overlay', overlayClassName, { 
            'dbb-country-filter-overlay-search': !!searchVal,
            'dbb-country-filter-overlay-en': isEn
          })}
          onSearch={setSearchVal}
          valueRender={(options) => {
            const leaf = options[options.length - 1];
            const label = leaf?.label || leaf?.value;
            return removeStrongTags(label);
          }}
          filter={(inputValue, option: ICountryFilterOption) => {
            if(filter) {
              return filter?.(inputValue, option);
            }
            // 中英文都匹配
            const { nameEn = '', nameCn = '' } = option || {};
            const val = inputValue?.toLocaleLowerCase();
            if (nameEn?.toLocaleLowerCase()?.includes(val) || nameCn?.toLocaleLowerCase()?.includes(val)) {
              return true;
            }
            return false;
          }}
        />
        <div className='dbb-country-filter-input-search'><SvgIcon type='search' /></div>
      </div>
    </CommonBubble>
  );
};

export default CountryFilter;
